import { type JSX } from 'react';
import { Activity } from 'lucide-react';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { SidebarNavSection } from './SidebarNavSection';

interface AgentsActivityNavGroupProps {
  /** Route the group (and its single child) navigates to. */
  to: string;
  /**
   * Whether this group is rendered in an ORGANIZATION context. The Browser
   * Agent Sites page is org-only (its view API is `/organizations/:id/...`, with
   * no personal variant), so its child is shown only when true. The personal
   * dashboard renders this group too (for Packages & Repos) and must NOT surface
   * a Browser Agent Sites link that would dead-end on Get Started (KIR-4703).
   */
  orgScoped?: boolean;
}

/**
 * AgentsActivityNavGroup — the "Agents Activity" sidebar section.
 *
 * Renders the parent row (Activity icon + label + NEW badge) with the
 * "Packages & Repos" page as its single child via {@link SidebarNavSection},
 * so it shares the section highlight behavior (selected child carries the
 * pill, active parent goes full-contrast — KIR-4159). Prompts (a future
 * child) will slot in alongside it.
 */
export function AgentsActivityNavGroup({
  to,
  orgScoped = false,
}: AgentsActivityNavGroupProps): JSX.Element {
  // KIR-4703: the Browser Agent Sites child is flag-gated AND org-only (the page
  // exists only under an organization). The backend keeps collecting regardless.
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.BROWSER_AGENT_ACTIVITY,
  ]);
  const showBrowserAgentSites =
    orgScoped && (flags[flagsmithFeatureFlags.BROWSER_AGENT_ACTIVITY] ?? false);

  return (
    <SidebarNavSection
      label="Agents Activity"
      to={to}
      icon={Activity}
      items={[
        { label: 'Packages & Repos', to },
        ...(showBrowserAgentSites
          ? [{ label: 'Browser Agent Sites', to: `${to}/browser-agent-sites` }]
          : []),
      ]}
      badge={
        <span className="ml-auto rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide text-white">
          New
        </span>
      }
    />
  );
}
